import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { projectsService, tasksService, usersService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Search, Plus, LayoutGrid, List, Kanban, X, Calendar } from "lucide-react";
import NewProjectPanel from "@/components/NewProjectPanel";
import ProjectCard from "@/components/ProjectCard";
import ProjectListRow from "@/components/ProjectListRow";
import KanbanBoard from "@/components/KanbanBoard";
import { cn } from "@/lib/utils";

const STATUSES = ["Planning", "Active", "On Hold", "In Review", "Completed", "Archived"];
const PRIORITIES = ["High", "Medium", "Low"];
const SORT_OPTIONS = [
  { value: "name-asc", label: "Name (A-Z)" },
  { value: "name-desc", label: "Name (Z-A)" },
  { value: "due-soonest", label: "Due Date (Soonest)" },
  { value: "due-latest", label: "Due Date (Latest)" },
  { value: "progress-high", label: "Progress (Highest)" },
  { value: "progress-low", label: "Progress (Lowest)" },
  { value: "newest", label: "Date Created (Newest)" },
  { value: "oldest", label: "Date Created (Oldest)" }
];

const Projects = () => {
  const { userData, hasPermission } = useAuth();
  const { toast } = useToast();
  const isCOO = userData?.role === "COO" || userData?.role === "CEO";
  
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [statusFilter, setStatusFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [teamMemberFilter, setTeamMemberFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortBy, setSortBy] = useState("newest");
  
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeStatFilter, setActiveStatFilter] = useState(null);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, tasksData, usersData] = await Promise.all([
          projectsService.getAll(),
          tasksService.getAll(),
          usersService.getAll()
        ]);
        
        let filteredProjects = projectsData;
        if (!isCOO) {
          filteredProjects = projectsData.filter(p => 
            p.assignedMembers?.includes(userData?.id) || 
            p.createdBy === userData?.id
          );
        }
        
        setProjects(filteredProjects);
        setTasks(tasksData);
        setTeamMembers(usersData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isCOO, userData?.id]);

  const handleStatusChange = async (projectId, newStatus) => {
    if (!isCOO) {
      toast({
        title: "Permission denied",
        description: "Only the COO can change project status",
        variant: "destructive"
      });
      return;
    }
    try {
      await projectsService.update(projectId, { status: newStatus });
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, status: newStatus } : p
      ));
      toast({
        title: "Status updated",
        description: `Project moved to ${newStatus}`,
        variant: "success"
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update project status",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProject = async (project) => {
    if (!isCOO) return;
    try {
      await projectsService.delete(project.id);
      setProjects(prev => prev.filter(p => p.id !== project.id));
      toast({
        title: "Project deleted",
        description: `${project.name} has been deleted`,
        variant: "success"
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive"
      });
    }
  };

  const handleDuplicateProject = async (project) => {
    if (!isCOO) return;
    try {
      const { id, ...projectData } = project;
      await projectsService.create({
        ...projectData,
        name: `${project.name} (Copy)`,
        status: "Planning"
      });
      const newProjects = await projectsService.getAll();
      setProjects(newProjects);
      toast({
        title: "Project duplicated",
        description: "A copy of the project has been created",
        variant: "success"
      });
    } catch (error) {
      console.error("Error duplicating project:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate project",
        variant: "destructive"
      });
    }
  };

  const handleArchiveProject = async (project) => {
    if (!isCOO) return;
    try {
      await projectsService.update(project.id, { status: "Archived" });
      setProjects(prev => prev.map(p => 
        p.id === project.id ? { ...p, status: "Archived" } : p
      ));
      toast({
        title: "Project archived",
        description: `${project.name} has been archived`,
        variant: "success"
      });
    } catch (error) {
      console.error("Error archiving project:", error);
      toast({
        title: "Error",
        description: "Failed to archive project",
        variant: "destructive"
      });
    }
  };

  const handleProjectCreated = () => {
    const fetchProjects = async () => {
      const data = await projectsService.getAll();
      setProjects(data);
    };
    fetchProjects();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setPriorityFilter("All");
    setTeamMemberFilter("All");
    setDateRange({ start: "", end: "" });
    setSortBy("newest");
    setActiveStatFilter(null);
  };

  const filteredProjects = useMemo(() => {
    let result = [...projects];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(term) || 
        p.description?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "All") {
      result = result.filter(p => p.status === statusFilter);
    }

    if (priorityFilter !== "All") {
      result = result.filter(p => p.priority === priorityFilter);
    }

    if (teamMemberFilter !== "All") {
      result = result.filter(p => p.assignedMembers?.includes(teamMemberFilter));
    }

    if (dateRange.start) {
      result = result.filter(p => p.startDate && new Date(p.startDate) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      result = result.filter(p => p.dueDate && new Date(p.dueDate) <= new Date(dateRange.end));
    }

    switch (sortBy) {
      case "name-asc":
        result.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "name-desc":
        result.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      case "due-soonest":
        result.sort((a, b) => new Date(a.dueDate || 9999999999999) - new Date(b.dueDate || 9999999999999));
        break;
      case "due-latest":
        result.sort((a, b) => new Date(b.dueDate || 0) - new Date(a.dueDate || 0));
        break;
      case "progress-high":
        result.sort((a, b) => (b.progress || 0) - (a.progress || 0));
        break;
      case "progress-low":
        result.sort((a, b) => (a.progress || 0) - (b.progress || 0));
        break;
      case "newest":
        result.sort((a, b) => new Date(b.createdAt?.toDate?.() || 0) - new Date(a.createdAt?.toDate?.() || 0));
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.createdAt?.toDate?.() || 0) - new Date(b.createdAt?.toDate?.() || 0));
        break;
      default:
        break;
    }

    return result;
  }, [projects, searchTerm, statusFilter, priorityFilter, teamMemberFilter, dateRange, sortBy]);

  const stats = useMemo(() => {
    const total = projects.length;
    const active = projects.filter(p => p.status === "Active").length;
    const completed = projects.filter(p => p.status === "Completed").length;
    const overdue = projects.filter(p => 
      p.dueDate && new Date(p.dueDate) < new Date() && p.status !== "Completed"
    ).length;
    const onHold = projects.filter(p => p.status === "On Hold").length;
    return { total, active, completed, overdue, onHold };
  }, [projects]);

  const paginatedProjects = useMemo(() => {
    if (viewMode === "kanban") return filteredProjects;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProjects.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProjects, currentPage, viewMode]);

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);

  const hasActiveFilters = searchTerm || statusFilter !== "All" || priorityFilter !== "All" || 
    teamMemberFilter !== "All" || dateRange.start || dateRange.end || activeStatFilter;

  const handleStatClick = (stat) => {
    setActiveStatFilter(stat);
    if (stat === "active") setStatusFilter("Active");
    else if (stat === "completed") setStatusFilter("Completed");
    else if (stat === "overdue") setStatusFilter("All");
    else if (stat === "onHold") setStatusFilter("On Hold");
    else setStatusFilter("All");
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <Skeleton className="h-14 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-72 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-navy">Projects</h1>
          <p className="text-gray-500 mt-1">Track and manage ongoing projects</p>
        </div>
        {isCOO && (
          <Button 
            onClick={() => setIsPanelOpen(true)}
            className="bg-navy hover:bg-navy-light"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200 focus:ring-action-blue/20"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              >
                <option value="All">All Status</option>
                {STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              >
                <option value="All">All Priority</option>
                {PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>

              <select
                value={teamMemberFilter}
                onChange={(e) => setTeamMemberFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              >
                <option value="All">All Team Members</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-36 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Start"
                />
                <span className="text-gray-400">-</span>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-36 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="End"
                />
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              >
                {SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                className={viewMode === "grid" ? "bg-navy text-white" : "text-gray-500 hover:text-navy"}
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                className={viewMode === "list" ? "bg-navy text-white" : "text-gray-500 hover:text-navy"}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "kanban" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("kanban")}
                className={viewMode === "kanban" ? "bg-navy text-white" : "text-gray-500 hover:text-navy"}
              >
                <Kanban className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">Active filters:</span>
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                  Search: {searchTerm}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchTerm("")} />
                </span>
              )}
              {statusFilter !== "All" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                  Status: {statusFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setStatusFilter("All")} />
                </span>
              )}
              {priorityFilter !== "All" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                  Priority: {priorityFilter}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => setPriorityFilter("All")} />
                </span>
              )}
              <Button variant="link" size="sm" onClick={clearFilters} className="text-action-blue">
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => { clearFilters(); setActiveStatFilter(null); }}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium transition-all",
            !activeStatFilter ? "bg-navy text-white" : "bg-white text-navy border border-slate-200 hover:bg-slate-50"
          )}
        >
          Total Projects: {stats.total}
        </button>
        <button
          onClick={() => handleStatClick("active")}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium transition-all",
            activeStatFilter === "active" ? "bg-navy text-white" : "bg-white text-navy border border-slate-200 hover:bg-slate-50"
          )}
        >
          Active: {stats.active}
        </button>
        <button
          onClick={() => handleStatClick("completed")}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium transition-all",
            activeStatFilter === "completed" ? "bg-navy text-white" : "bg-white text-navy border border-slate-200 hover:bg-slate-50"
          )}
        >
          Completed: {stats.completed}
        </button>
        <button
          onClick={() => handleStatClick("overdue")}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium transition-all",
            activeStatFilter === "overdue" ? "bg-navy text-white" : "bg-white text-navy border border-slate-200 hover:bg-slate-50"
          )}
        >
          <span className={stats.overdue > 0 ? "text-red-600" : ""}>Overdue: {stats.overdue}</span>
        </button>
        <button
          onClick={() => handleStatClick("onHold")}
          className={cn(
            "px-4 py-2 rounded-full text-sm font-medium transition-all",
            activeStatFilter === "onHold" ? "bg-navy text-white" : "bg-white text-navy border border-slate-200 hover:bg-slate-50"
          )}
        >
          On Hold: {stats.onHold}
        </button>
      </div>

      {filteredProjects.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Kanban className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-navy mb-2">
              {hasActiveFilters ? "No Projects Match Your Filters" : "No Projects Yet"}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {hasActiveFilters 
                ? "Try adjusting your search or filters to find what you're looking for."
                : "Start by creating your first project to track and manage your team's work."
              }
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button onClick={() => setIsPanelOpen(true)} className="bg-navy hover:bg-navy-light">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedProjects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={handleDeleteProject}
                onDuplicate={handleDuplicateProject}
                onArchive={handleArchiveProject}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i + 1}
                  variant={currentPage === i + 1 ? "default" : "outline"}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? "bg-navy" : ""}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      ) : viewMode === "list" ? (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-[auto_1fr_100px_80px_120px_100px_80px_40px] gap-4 p-4 border-b border-slate-200 font-semibold text-sm text-gray-500">
              <div className="w-3" />
              <div>Project</div>
              <div>Status</div>
              <div>Priority</div>
              <div>Progress</div>
              <div>Team</div>
              <div>Due Date</div>
              <div>Tasks</div>
            </div>
            {paginatedProjects.map(project => (
              <ProjectListRow
                key={project.id}
                project={project}
                onDelete={handleDeleteProject}
                onDuplicate={handleDuplicateProject}
                onArchive={handleArchiveProject}
              />
            ))}
          </CardContent>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-slate-200">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </Button>
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i + 1}
                  variant={currentPage === i + 1 ? "default" : "outline"}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? "bg-navy" : ""}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <KanbanBoard
          projects={filteredProjects}
          onStatusChange={handleStatusChange}
        />
      )}

      <NewProjectPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        onSuccess={handleProjectCreated}
      />
    </div>
  );
};

export default Projects;